import Plotly, {PlotData} from "plotly.js"
import createPlotlyComponent from 'react-plotly.js/factory';
import * as csDictionary from 'plotly.js/lib/locales/it';
import {WindBioEnergySeries, TimeSeriesItem} from '../../Services/typescript-fetch-client';
import React, {useEffect, useState} from "react";
import {Figure} from "react-plotly.js";
import { ZoomElement } from "../../Models/ZoomElement";
import {DateTime} from "luxon";
import Icons from "plotly.js/src/fonts/ploticon";
import {LegendSerie} from "../PlotsSharedLegend/PlotsSharedLegend";

type Props = {
    windBioEnergySeries: WindBioEnergySeries,
    viewName: string,
    maxY: number,
    isLandscape: boolean,
    isSinglePlantView: boolean,
    visibleSeries?: Map<string, LegendSerie>,
    zoom: ZoomElement,
    setZoom: React.Dispatch<React.SetStateAction<ZoomElement | null>>,
    zoomBack: () => void,
    backToInitialZoom: () => void,
};

const getZoomDateRange = (zoom: ZoomElement | null | undefined) => {
    return zoom?.x[0]?.isValid && zoom?.x[1]?.isValid
        ? zoom.x.map((v: DateTime) => v.toJSDate())
        : undefined;
};

const RenewRealTimePlotlyComponent = (props: Props) => {
    let [plotState, setPlotState] = useState<Readonly<Figure>>(() => {
        return {
            layout: {
                showlegend: false,
                legend: { "orientation": "h" },
                margin: {
                    l: 35,
                    r: 15,
                    b: 37,
                    t: 25,
                    pad: 5
                },
                uirevision:'true',
                hovermode: "x",
                xaxis: {
                    //autorange: true,
                    tickformat: '',
                    range: getZoomDateRange(props.zoom)
                },
                yaxis: {
                    range: props.zoom?.y ? [...props.zoom?.y] : undefined
                },
                datarevision: 0
            },
            data: props.windBioEnergySeries ? [
                {
                    x: props.windBioEnergySeries.prodotto ? props.windBioEnergySeries.prodotto
                        .map((timeSeriesItem: TimeSeriesItem) => (timeSeriesItem.dateKey) ? timeSeriesItem.dateKey.toString() : null) : [],
                    y: props.windBioEnergySeries.prodotto ? props.windBioEnergySeries.prodotto
                        .map((timeSeriesItem: TimeSeriesItem) => (typeof(timeSeriesItem.value) != "undefined" && timeSeriesItem.value !== null) ? timeSeriesItem.value! : 
                            props.windBioEnergySeries.badProdotto?.filter((tsBad: TimeSeriesItem) => tsBad.dateKey === timeSeriesItem.dateKey)[0]?.value != 0 ? 0 : NaN) : [],
                    type: 'scatter',
                    mode: "lines",
                    fill: 'tozeroy',
                    name: 'Prodotto',
                    //hoverinfo: 'y+name',
                    line: {
                        shape: 'hv',
                        color: '#97D700',
                        width: 1.5
                    }
                },
                {
                    x: props.windBioEnergySeries.forecast ? props.windBioEnergySeries.forecast.map((timeSeriesItem: TimeSeriesItem) => (timeSeriesItem.dateKey) ? timeSeriesItem.dateKey.toString() : null) : [],
                    y: props.windBioEnergySeries.forecast ? props.windBioEnergySeries.forecast.map((timeSeriesItem: TimeSeriesItem) => (typeof(timeSeriesItem.value) != "undefined" && timeSeriesItem.value !== null) ?
                        timeSeriesItem.value! : 0) : [],
                    type: 'scatter',
                    mode: "lines",
                    name: 'Forecast',
                    //hoverinfo: 'y+name',
                    line: {
                        shape: 'hv',
                        color: '#0f226d',
                        width: 1.5
                    }
                },
                {
                    x: props.windBioEnergySeries.pv ? props.windBioEnergySeries.pv.map((timeSeriesItem: TimeSeriesItem) => (timeSeriesItem.dateKey) ? timeSeriesItem.dateKey.toString() : null) : [],
                    y: props.windBioEnergySeries.pv ? props.windBioEnergySeries.pv.map((timeSeriesItem: TimeSeriesItem) => (typeof(timeSeriesItem.value) != "undefined" && timeSeriesItem.value !== null) ?
                        timeSeriesItem.value! : 0) : [],
                    type: 'scatter',
                    mode: "lines",
                    name: 'PV',
                    //hoverinfo: 'y+name',
                    line: {
                        color: '#ea3a3e',
                        shape: 'hv',
                        width: 1.5
                    }
                },
                {
                    x: props.windBioEnergySeries.nominalPower ? props.windBioEnergySeries.nominalPower.map((timeSeriesItem: TimeSeriesItem) => (timeSeriesItem.dateKey) ? timeSeriesItem.dateKey.toString() : null) : [],
                    y: props.windBioEnergySeries.nominalPower ? props.windBioEnergySeries.nominalPower.map((timeSeriesItem: TimeSeriesItem) => (typeof(timeSeriesItem.value) != "undefined" && timeSeriesItem.value !== null) ?
                        timeSeriesItem.value! : 0) : [],
                    type: 'scatter',
                    mode: "lines",
                    //hoverinfo: 'y+name',
                    name: 'P_Nom',
                    line: {
                        shape: 'hv',
                        color: '#777777',
                        width: 1,
                        dash: "dash"
                    }
                },
                {
                    x: props.windBioEnergySeries.badProdotto ? props.windBioEnergySeries.badProdotto.map((timeSeriesItem: TimeSeriesItem) => (timeSeriesItem.dateKey) ? timeSeriesItem.dateKey.toString() : null) : [],
                    y: props.windBioEnergySeries.badProdotto ? props.windBioEnergySeries.badProdotto.map((timeSeriesItem: TimeSeriesItem) => (typeof(timeSeriesItem.value) != "undefined" && timeSeriesItem.value !== null) ?
                        timeSeriesItem.value! : 0) : [],
                    type: 'scatter',
                    mode: "none",
                    fill: "tozeroy",
                    name: 'BAD',
                    fillcolor: '#fcaf4999',
                    hoverinfo: 'x',
                    showlegend: false,
                    line: {
                        shape: 'vh'
                    }
                }
            ] : [],
            frames: []
        };
    });

    useEffect(() => {
        setPlotState(prev => {
            let temp = {
                ...prev
            };
            
            let activeSeries : (string[] | null) = typeof props.visibleSeries === "undefined" ? null : [];
            props.visibleSeries?.forEach((v, k) => {
                if(v.active){
                    activeSeries = (activeSeries ?? []).concat(v.controlledSeries);
                }
            });

            for(let i = 0; i < temp.data.length; i++){
                if(activeSeries === null || activeSeries.indexOf(temp.data[i].name ?? "") !== -1){
                    (temp.data[i] as Partial<PlotData>).visible = true;
                }
                else{
                    (temp.data[i] as Partial<PlotData>).visible = 'legendonly';
                }

                if(typeof props.visibleSeries !== "undefined" && props.visibleSeries.get(temp.data[i].name ?? "")?.color){
                    if((temp.data[i] as Partial<PlotData>).fillcolor) {
                        (temp.data[i] as Partial<PlotData>).fillcolor = props.visibleSeries.get(temp.data[i].name ?? "")?.color;
                    }
                    else if((temp.data[i] as Partial<PlotData>)?.line?.color){
                        (temp.data[i] as Partial<PlotData>).line!.color = props.visibleSeries.get(temp.data[i].name ?? "")?.color;
                    }
                }
            }
            prev.layout.datarevision = Number(prev.layout.datarevision!) + 1;

            return temp;
        });
    }, [props.visibleSeries]);

    useEffect(() => {
        if(props.zoom) {
            setPlotState(prev => {
                let temp = {
                    ...prev
                };

                const zoomDateRange = getZoomDateRange(props.zoom);
                if(zoomDateRange) {
                    temp.layout.xaxis!.range = zoomDateRange;
                    temp.layout.xaxis!.autorange = false;
                }
                else{
                    temp.layout.xaxis!.range = undefined;
                    temp.layout.xaxis!.autorange = true;
                }
                temp.layout.yaxis!.range = [...props.zoom.y];
                temp.layout.datarevision = Number(prev.layout.datarevision!) + 1;
                return temp;
            });
        }
    }, [props.zoom]);
    
    const setNewZoom = (eventData: Readonly<Plotly.PlotRelayoutEvent>) => {
        if(eventData["xaxis.range[0]"] || eventData["xaxis.range[1]"] || eventData["yaxis.range[0]"] || eventData["yaxis.range[1]"]){
            props.setZoom(prev => {
                return {
                    x: [
                        eventData["xaxis.range[0]"] ?
                            DateTime.fromSQL(eventData["xaxis.range[0]"] as unknown as string) :
                            (prev ?
                                prev.x[0] :
                                DateTime.now().startOf("day")),
                        eventData["xaxis.range[1]"] ?
                            DateTime.fromSQL(eventData["xaxis.range[1]"] as unknown as string) :
                            (prev ?
                                prev.x[1] :
                                DateTime.now().startOf("day"))
                    ],
                    y: [
                        eventData["yaxis.range[0]"] ?
                            Number(eventData["yaxis.range[0]"]) :
                            (prev ?
                                prev.y[0] : 0),
                        eventData["yaxis.range[1]"] ?
                            Number(eventData["yaxis.range[1]"]) :
                            (prev ?
                                prev.y[1] : 0),
                    ]
                };
            });
        }
    }
    
    const Plot = createPlotlyComponent(Plotly);

    let graphClass = "graph" + (props.isLandscape ? "Landscape": "Portrait") + (props.isSinglePlantView ? "Single" : "Multiple");

    return (
        <Plot
            data={plotState.data}
            layout={plotState.layout}
            config = {{
                displaylogo: false,
                // @ts-ignore
                locales: { 'it-IT': csDictionary },//make locale available to the plot
                locale: 'it-IT',//use the locale
                modeBarButtonsToRemove: [
                    'zoom2d',
                    'pan2d',
                    'zoomIn2d',
                    'zoomOut2d',
                    'autoScale2d',
                    'resetScale2d'
                ],
                modeBarButtonsToAdd: [
                    {
                        name: 'previousZoomLevel',
                        title: 'Zoom Indietro',
                        icon: Icons.zoom_minus,
                        click: (gd, ev) => {
                            props.zoomBack();
                        }
                    },
                    {
                        name: 'newResetScale',
                        title: 'Reset zoom',
                        icon: Icons.home,
                        click: (gd, ev) => {
                            props.backToInitialZoom();
                        }
                    }
                ],
                toImageButtonOptions: {
                    filename: props.viewName
                },
                showAxisDragHandles: false,
                doubleClick: false
            }}
            useResizeHandler={true}
            className={graphClass + " RenewGraph"}
            onRelayout={setNewZoom}
        />
    );
}

export default RenewRealTimePlotlyComponent;
